import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CipherService } from './cipher.service';

export interface OrdenPedidoConsulta {
  codigo?: number;
  mensaje?: string;
  ordenPedidoId?: number | string;
  tipoPersona?: string;
  tipoCliente?: string;
  datosBasicos?: Record<string, any>;
  detallesCertificado?: Record<string, any> | Record<string, any>[];
  solicitantes?: Array<Record<string, any>>;
  informacionEnvio?: Record<string, any>;
  [key: string]: unknown;
}

export interface DownloadSessionContext {
  ordenPedidoId: string;
  solicitudTitularId: number;
  mecanismo: string;
  usaDobleAutenticacion: boolean;
  correo: string;
  nombreCompleto: string;
  /** Para saludos cortos (pueden venir vacíos). */
  primerNombre: string;
  primerApellido: string;
  tipoIdentificacion: string;
  numeroIdentificacion: string;
  existeCSR: boolean;
}

export interface ObtenerCertificadoRequest {
  solicitudId: number;
  contrasena: string;
  tokenRse?: string;
  tipoCertificado?: 'P10' | 'V';
}

export interface ObtenerCertificadoResponse {
  estado?: string;
  tipo?: string;
  mensaje?: string;
  certificadoB64?: string;
  nombreArchivo?: string;
  [key: string]: unknown;
}

export interface Ccsw5RegistroResponse {
  success?: boolean;
  message?: string;
  errorCode?: string;
}

/**
 * Descarga de certificado (portado desde Canal de Ventas).
 * TV con código: consultar orden → OTP → CCSW5 → obtener certificado.
 * Orden/certificado: body cifrado `{ data }` (misma clave `environment.cipher`).
 */
@Injectable({ providedIn: 'root' })
export class CertificateDownloadService {
  private readonly ordenBase = environment.ordenPedidoServiceUrl;
  private readonly certBase = environment.certificadoServiceUrl;
  private readonly otpUrl = environment.otpGatewayUrl;
  private readonly ccsw5Base = environment.urlCcsw5;

  constructor(
    private http: HttpClient,
    private cipher: CipherService
  ) {}

  consultarOrdenPedido(ordenPedidoId: string | number): Observable<OrdenPedidoConsulta> {
    // Mismo contrato que Canal: cifra el body tal cual (id numérico/string).
    return this.postEncrypted<OrdenPedidoConsulta>(
      `${this.ordenBase}/ordenPedido/consultar`,
      ordenPedidoId
    ).pipe(map((res) => this.normalizarDetallesCertificado(res)));
  }

  obtenerCertificado(request: ObtenerCertificadoRequest): Observable<ObtenerCertificadoResponse> {
    return this.postEncrypted<ObtenerCertificadoResponse>(
      `${this.certBase}/certificado/obtener`,
      request
    );
  }

  solicitarOtp(toAddress: string): Observable<{ statusCode?: number; OTPSha256?: string; responseText?: string }> {
    const body = {
      fromAddress: environment.mailOtp,
      toAddress,
      fromName: 'Operaciones',
      subject: 'Verificación de identidad vía OTP',
      bodyContent:
        '<html><head><title>C&oacute;digo de verificaci&oacute;n</title></head><body>' +
        '<p class="title"><b>C&oacute;digo de verificaci&oacute;n</b></p>' +
        '<p class="story">Su c&oacute;digo de verificaci&oacute;n es: CODE</p></body></html>',
      certification: false,
      type: 'EMAIL'
    };
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'x-sckey-token': environment.otpTokenHeader
      })
    };
    return this.http.post<{ statusCode?: number; OTPSha256?: string; responseText?: string }>(
      this.otpUrl,
      body,
      httpOptions
    );
  }

  registrarUsuarioCcsw5(request: {
    email: string;
    name: string;
    username: string;
    password: string;
  }): Observable<HttpResponse<Ccsw5RegistroResponse>> {
    const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    return this.http.post<Ccsw5RegistroResponse>(
      `${this.ccsw5Base}/auth/registro`,
      request,
      { ...httpOptions, observe: 'response' as const }
    );
  }

  esRegistroCcsw5Aceptable(status: number, body?: Ccsw5RegistroResponse | null): boolean {
    if (status === 409 || body?.errorCode === 'USER_ALREADY_EXISTS') {
      return true;
    }
    if (status >= 200 && status < 300) {
      return body?.success !== false;
    }
    return false;
  }

  async sha256(message: string): Promise<string> {
    const data = new TextEncoder().encode(message);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Cifra el body JSON y envía `{ data }`. Descifra la respuesta si viene envuelta.
   * Misma clave/formato que Canal / reset-password / sign.
   */
  private postEncrypted<T>(url: string, body: unknown): Observable<T> {
    const headers = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    if (!this.cipher.isEncryptionEnabled) {
      return this.http.post<T>(url, body, headers);
    }

    return from(this.cipher.encrypt(JSON.stringify(body))).pipe(
      switchMap((encrypted) =>
        this.http.post<{ data?: string } | T>(url, { data: encrypted }, headers)
      ),
      switchMap((response) => this.unwrapCipherResponse<T>(response))
    );
  }

  private unwrapCipherResponse<T>(response: { data?: string } | T): Observable<T> {
    const wrapped = response as { data?: unknown };
    if (wrapped?.data && typeof wrapped.data === 'string') {
      return from(this.cipher.decrypt(wrapped.data)).pipe(
        map((plain) => JSON.parse(plain) as T)
      );
    }
    return of(response as T);
  }

  /**
   * Construye el contexto de descarga.
   * - codigo: Token virtual con doble autenticación (OTP + CCSW5).
   * - clasico: Token virtual sin doble autenticación (OTP + contraseña).
   * - p10: PKCS#10 (OTP + llave privada + .cer).
   */
  buildDownloadSession(
    res: OrdenPedidoConsulta,
    ordenPedidoIdRaw: string,
    modo: 'codigo' | 'clasico' | 'p10'
  ): DownloadSessionContext {
    const solicitantes = Array.isArray(res.solicitantes) ? res.solicitantes : [];
    const db = res.datosBasicos ?? {};
    const dc = (res.detallesCertificado ?? {}) as Record<string, any>;
    const sol0 = solicitantes[0] ?? {};

    if (!solicitantes.length || !res.datosBasicos || !res.detallesCertificado) {
      throw new Error('La orden está incompleta: faltan solicitantes, datos básicos o detalles del certificado.');
    }

    const titularId = sol0['solicitudTitularId'];
    if (titularId == null) {
      throw new Error('No fue posible obtener el identificador del titular.');
    }

    const mecanismo = String(dc['mecanismoEntrega'] ?? '').trim();
    const usaDoble = +sol0['usaDobleAutenticacion'] === 1;

    if (modo === 'p10') {
      if (!this.esPkcs10(mecanismo)) {
        throw new Error(
          `Esta sección es solo para P10 / PKCS#10. El mecanismo de la orden es «${mecanismo || 'sin valor'}».`
        );
      }
    } else {
      if (!this.esTokenVirtual(mecanismo)) {
        throw new Error(
          `Esta sección es solo para Token virtual. El mecanismo de la orden es «${mecanismo || 'sin valor'}».`
        );
      }
      if (modo === 'codigo' && !usaDoble) {
        throw new Error(
          'Esta orden es Token virtual sin código de verificación. ' +
            'Usa Administración → Token virtual para ese flujo.'
        );
      }
      if (modo === 'clasico' && usaDoble) {
        throw new Error(
          'Esta orden es Token virtual con código de verificación. ' +
            'Usa Administración → Token virtual (código de verificación).'
        );
      }
    }

    const correo =
      String(db['correoElectronico'] ?? sol0['correoElectronico'] ?? sol0['correo'] ?? '').trim();
    if (!correo) {
      throw new Error('La orden no trae correo del titular para enviar el código OTP.');
    }

    const nombreCompleto = this.armarNombreCompleto(db, sol0);
    const primerNombre = String(sol0['primerNombre'] ?? db['primerNombre'] ?? '').trim();
    const primerApellido = String(sol0['primerApellido'] ?? db['primerApellido'] ?? '').trim();

    const numeroIdentificacion = String(
      sol0['numeroIdentificacion'] ?? db['numeroIdentificacion'] ?? ''
    ).trim();
    if (!numeroIdentificacion) {
      throw new Error('La orden no trae el número de identificación de la persona (titular).');
    }

    return {
      ordenPedidoId: String(res.ordenPedidoId ?? ordenPedidoIdRaw),
      solicitudTitularId: Number(titularId),
      mecanismo,
      usaDobleAutenticacion: usaDoble,
      correo,
      nombreCompleto,
      primerNombre,
      primerApellido,
      tipoIdentificacion: String(sol0['tipoIdentificacion'] ?? db['tipoIdentificacion'] ?? ''),
      numeroIdentificacion,
      existeCSR: this.normalizarExisteCsr(dc['existeCSR'])
    };
  }

  /** @deprecated Preferir buildDownloadSession. */
  buildTvSession(
    res: OrdenPedidoConsulta,
    ordenPedidoIdRaw: string,
    modo: 'codigo' | 'clasico'
  ): DownloadSessionContext {
    return this.buildDownloadSession(res, ordenPedidoIdRaw, modo);
  }

  /** @deprecated Preferir buildDownloadSession(..., 'codigo'). */
  buildTvCodigoSession(res: OrdenPedidoConsulta, ordenPedidoIdRaw: string): DownloadSessionContext {
    return this.buildDownloadSession(res, ordenPedidoIdRaw, 'codigo');
  }

  /** Genera llave privada PKCS#10 (body cifrado `{ data }`, igual que consultar/obtener). */
  generarLlavePrivada(solicitudId: number): Observable<unknown> {
    return this.postEncrypted<unknown>(`${this.ordenBase}/generador/generarLlavePrivada`, {
      solicitudId
    });
  }

  /**
   * Une .key + .cer + contraseña → PFX (JoinKeys / APPGenerate).
   * Body en claro, igual que Canal de Ventas (`UNIRLLAVES`).
   */
  unirLlaves(request: { crt: string; key: string; password: string }): Observable<{ pfx?: string }> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };
    return this.http.post<{ pfx?: string }>(environment.joinKeysUrl, request, httpOptions);
  }

  ofuscarCorreo(mail: string): string {
    const m = String(mail || '');
    if (m.length >= 10) {
      return m.substring(0, 3) + '******' + m.substring(m.length - 7);
    }
    return m ? `${m.substring(0, 1)}******` : '';
  }

  extraerAdjuntoLlavePrivada(res: any): { mime: string; name: string; data: string } | null {
    let payload = res?.data ?? res;
    if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      } catch {
        return null;
      }
    }
    const adjunto = payload?.adjunto;
    if (!adjunto || typeof adjunto.data !== 'string' || !String(adjunto.data).trim()) {
      return null;
    }
    return {
      mime: typeof adjunto.mime === 'string' ? adjunto.mime : 'application/pkcs8',
      name: typeof adjunto.name === 'string' ? adjunto.name : 'llave_privada.key',
      data: String(adjunto.data)
    };
  }

  private esTokenVirtual(mecanismo: string): boolean {
    const u = mecanismo.toUpperCase();
    return u.includes('TOKEN') && u.includes('VIRTUAL');
  }

  private esPkcs10(mecanismo: string): boolean {
    const u = mecanismo.toUpperCase().replace(/\s/g, '');
    return u === 'PKCS#10' || u === 'PKCS10' || (u.includes('PKCS') && u.includes('10'));
  }

  private normalizarExisteCsr(value: unknown): boolean {
    if (value === true || value === 1) return true;
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase();
      return v === '1' || v === 'true' || v === 's' || v === 'si' || v === 'sí';
    }
    return false;
  }

  private normalizarDetallesCertificado(resp: OrdenPedidoConsulta): OrdenPedidoConsulta {
    if (!resp || typeof resp !== 'object') {
      return resp;
    }
    const dc = resp.detallesCertificado;
    if (!Array.isArray(dc)) {
      return resp;
    }
    resp.detallesCertificado = dc.length > 0 ? dc[0] : undefined;
    return resp;
  }

  private armarNombreCompleto(
    db: Record<string, any>,
    sol: Record<string, any>
  ): string {
    // Preferir partes del titular/solicitante (pueden faltar 1–3 campos).
    const partes = [
      sol['primerNombre'] ?? db['primerNombre'],
      sol['segundoNombre'] ?? db['segundoNombre'],
      sol['primerApellido'] ?? db['primerApellido'],
      sol['segundoApellido'] ?? db['segundoApellido']
    ]
      .map((p) => (p != null ? String(p).trim() : ''))
      .filter((p) => p !== '');
    if (partes.length) {
      return partes.join(' ').replace(/\s+/g, ' ').trim();
    }
    return String(sol['nombreCompleto'] ?? db['nombreCompleto'] ?? '').trim();
  }
}
